import { PAGES } from "../../features/navigation/types/pages.constant";
import { CLEAR_ERRORS } from "../../constants/types";
import { GenericRootThunkAction } from "../../types/common";
import backend from "../../services/axios.functions";
import {
  ExecutionBriefsState,
  TExecutionBriefResults as ExecutionProps,
} from "../reducers/executionBriefsReducer";
import { errorMessage, successMessage } from "./messagesActions";
import { RESULTS_PER_PAGE } from "../../utils/constants";
import { formatDate } from "../../utils/date-format.utils";
import { ApprovalStatus } from "../../types/approval-status/approval-status.type";
import { handleError, createError } from "./errorHandlingAction";
import mapParameters from "../../utils/mapParameters";
import store from "../../redux/store";
import { LOADING_STATES } from "../../features/loading";

export const SET_EXECUTION_BRIEF_DATA = "SET_EXECUTION_BRIEF_DATA";
export const SET_EXECUTION_BRIEF_ERRORS = "SET_EXECUTION_BRIEF_ERRORS";

// execution-briefs reducer types
export const SET_EXECUTION_BRIEF_LOADING = "SET_EXECUTION_BRIEF_LOADING";
export const EXECUTION_BRIEF_PAGE_CHANGE = "EXECUTION_BRIEF_PAGE_CHANGE";
export const FETCH_EXECUTION_BRIEF_FILTERS = "FETCH_EXECUTION_BRIEF_FILTERS";
export const FETCH_EXECUTION_BRIEF_FILTERS_SUCCESS = "FETCH_EXECUTION_BRIEF_FILTERS_SUCCESS";
export const SET_EXECUTION_BRIEF_STATUS = "SET_EXECUTION_BRIEF_STATUS";
export const SET_EXECUTION_BRIEF_PARENT_STATUS = "SET_EXECUTION_BRIEF_PARENT_STATUS";
export const LOADING_EXECUTION_BRIEFS = "LOADING_EXECUTION_BRIEFS";
export const LIST_EXECUTION_BRIEFS = "LIST_EXECUTION_BRIEFS";
export const SET_EXECUTION_BRIEFS_FOR_CAMPAIGN = "SET_EXECUTION_BRIEFS_FOR_CAMPAIGN";

export const SHOW_NEW_EXECUTION_BRIEF_MODAL = "SHOW_NEW_EXECUTION_BRIEF_MODAL";
export const HIDE_NEW_EXECUTION_BRIEF_MODAL = "HIDE_NEW_EXECUTION_BRIEF_MODAL";

export const CLEAR_EXECUTION_BRIEF_ERRORS = "CLEAR_EXECUTION_BRIEF_ERRORS";
export const RESET_EXECUTION_BRIEF_ATTRIBUTE = "RESET_EXECUTION_BRIEF_ATTRIBUTE";
export const RESET_EXECUTION_BRIEF = "RESET_EXECUTION_BRIEF";

// Success/Failure New Execution Briefs
export const SUCCESS_NEW_EXECUTION_PAGE = "SUCCESS_NEW_EXECUTION_PAGE";
export const FAILURE_NEW_EXECUTION_PAGE = "FAILURE_NEW_EXECUTION_PAGE";

// Success/Failure Update Execution Page
export const SUCCESS_UPDATE_EXECUTION_PAGE = "SUCCESS_UPDATE_EXECUTION_PAGE";
export const SUCCESS_RENAME_EXECUTION_PAGE = "SUCCESS_RENAME_EXECUTION_PAGE";
export const FAILURE_UPDATE_EXECUTION_PAGE = "FAILURE_UPDATE_EXECUTION_PAGE";

// Language Actions
export const SET_LANGUAGE_OPTIONS = "SET_LANGUAGE_OPTIONS";
export const SET_LANGUAGE_OPTIONS_ERRORS = "SET_LANGUAGE_OPTIONS_ERRORS";

// Marketing Channels
export const SET_MARKETING_CHANNELS = "SET_MARKETING_CHANNELS";
export const SET_MARKETING_CHANNELS_ERRORS = "SET_MARKETING_CHANNELS_ERRORS";

// Region Actions
export const SET_REGION_OPTIONS = "SET_REGION_OPTIONS";
export const SET_REGION_OPTIONS_ERRORS = "SET_REGION_OPTIONS_ERRORS";

export const getExecutionBriefFilters = () => async (dispatch: any) => {
  dispatch({ type: FETCH_EXECUTION_BRIEF_FILTERS });
  try {
    const { data: payload } = await backend.get("/execution-briefs/filter-options");
    if (payload) {
      dispatch({
        type: FETCH_EXECUTION_BRIEF_FILTERS_SUCCESS,
        payload,
      });
    }
  } catch (err) {
    console.error(err);
  }
};

const paramMap = {
  briefOwner: "brief_owner",
  dateFrom: "created_at_after",
  dateTo: "created_at_before",
  regions: "regions",
  status: "status",
  template: "template",
};

const defaultParams = {
  limit: [RESULTS_PER_PAGE],
  page: [1],
};

export const getExecutionBriefList = (params: any = {}, pageChange: boolean = false) => async (
  dispatch: any
) => {
  const queryString = mapParameters({ ...defaultParams, ...params }, paramMap);

  if (pageChange) {
    dispatch({ type: EXECUTION_BRIEF_PAGE_CHANGE });
  } else {
    dispatch({ type: LOADING_EXECUTION_BRIEFS });
  }

  try {
    const { data } = await backend.get(`/execution-briefs/${queryString}`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: LIST_EXECUTION_BRIEFS,
      payload: data,
    });
  } catch (err) {
    console.log(err);
  }
};

export const getExecutionBriefsInCampaign = (
  campaignBriefId: string
): GenericRootThunkAction => async (dispatch) => {
  dispatch({ type: LOADING_EXECUTION_BRIEFS });
  try {
    const { data } = await backend.get("/execution-briefs/", {
      params: { campaign_brief_id: campaignBriefId },
    });
    if (!data || !data.results) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_EXECUTION_BRIEFS_FOR_CAMPAIGN,
      payload: { executionBriefs: data.results },
    });
  } catch (error) {
    handleError(error, dispatch, SET_EXECUTION_BRIEF_ERRORS);
  }
};

export const getExecutionBrief = (id: string): GenericRootThunkAction => async (dispatch) => {
  try {
    dispatch({ type: LOADING_EXECUTION_BRIEFS });
    const { data } = await backend.get(`/execution-briefs/${id}/`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_EXECUTION_BRIEF_DATA,
      payload: {
        data,
      },
    });

    const {
      data: { status: campaignStatus },
    } = await backend.get(`/campaign-briefs/${data.campaignBrief}/`);
    dispatch(setExecutionBriefParentStatus(campaignStatus));
  } catch (error) {
    handleError(error, dispatch, SET_EXECUTION_BRIEF_ERRORS, {
      404: { message: "This Multi Channel Brief does not exist." },
    });
  }
};

export const getLanguageOptions = (): GenericRootThunkAction => async (dispatch) => {
  try {
    const { data } = await backend.get(`/language-options/`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_LANGUAGE_OPTIONS,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    handleError(error, dispatch, SET_LANGUAGE_OPTIONS_ERRORS);
  }
};

export const getMarketingChannelOptions = (): GenericRootThunkAction => async (dispatch) => {
  try {
    const { data } = await backend.get(`/marketing-channels/`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_MARKETING_CHANNELS,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    handleError(error, dispatch, SET_MARKETING_CHANNELS_ERRORS);
  }
};

export const getRegionOptions = () => async () => {
  try {
    const { data } = await backend.get(`/region-options/`);
    if (!data) {
      throw new Error("No data in network response");
    }

    store.dispatch({ type: CLEAR_ERRORS });
    store.dispatch({
      type: SET_REGION_OPTIONS,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    handleError(error, store.dispatch, SET_REGION_OPTIONS_ERRORS);
  }
};

export const resetExecutionAttributeText = (attributeName: string) => ({
  type: RESET_EXECUTION_BRIEF_ATTRIBUTE,
  payload: { attributeName },
});

export const resetExecutionBrief = () => ({
  type: RESET_EXECUTION_BRIEF,
});

export const saveExecutionBrief = (
  params: { campaignBrief: number; name: string; resourceType: string },
  history?: any
): GenericRootThunkAction => async (dispatch) => {
  // Save the Execution Brief reducer
  try {
    dispatch({ type: SET_EXECUTION_BRIEF_LOADING, payload: LOADING_STATES.loading });

    const { data } = await backend.post("/execution-briefs/", params);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({ type: SUCCESS_NEW_EXECUTION_PAGE, payload: data });
    const { id } = data;
    if (typeof id !== "undefined" && history) {
      history.push(`${PAGES.EXECUTION_BRIEF.URL}/${id}`);
    }
  } catch (error) {
    handleError(error, dispatch, FAILURE_NEW_EXECUTION_PAGE);
  }
};

const resetExecutionBriefStatus = (id: number): GenericRootThunkAction => async (dispatch) => {
  try {
    await backend.put(`execution-briefs/${id}/reset/`);
    dispatch(setExecutionBriefStatus("draft"));
    dispatch(successMessage("Successfully restarted execution."));
  } catch (error) {
    handleError(error, dispatch, FAILURE_UPDATE_EXECUTION_PAGE);
    dispatch(errorMessage(createError(error).errorMessage));
  }
};

function mapExecutionPropsToParams(form: ExecutionProps) {
  return {
    areVendorAssetsProvided: form.areVendorAssetsProvided,
    audience: form.audience,
    background: form.background,
    businessObjectives: form.businessObjectives,
    campaignBrief: form.campaignBrief,
    copyDirection: form.copyDirection,
    creativeDirection: form.creativeDirection,
    endAt: formatDate(form.endAt),
    executionDetails: form.executionDetails,
    flyerPeriod: form.flyerPeriod,
    hasVendorCommitment: form.hasVendorCommitment,
    isInventoryAvailable: form.isInventoryAvailable,
    isRegional: form.isRegional,
    keyMessage: form.keyMessage,
    languages: form.languages,
    merchOwners: form.merchOwnerDetails.map((merchOwner) => merchOwner.id),
    name: form.executionName,
    numberOfWorkOrdersRequested: form.numberOfWorkOrdersRequested,
    programObjectives: form.programObjectives,
    projectOverview: form.projectOverview,
    regions: form.regions,
    reportingMasterCampaignName: form.reportingMasterCampaignName,
    resourcetype: form.executionTemplate,
    startAt: formatDate(form.startAt),
    storeNumber: form.storeNumber,
    storeType: form.storeType,
    vendorAssetsLocation: form.vendorAssetsLocation,
  };
}

export const updateExecutionBrief = (form: ExecutionProps): GenericRootThunkAction => async (
  dispatch,
  getState
) => {
  const state = getState().executionBriefs as ExecutionBriefsState;

  // Save the Execution Brief reducer
  const params = mapExecutionPropsToParams(form);

  // Change from "In Progress" -> "Draft"
  if (state.data.status === "in_progress" && form.status === "draft") {
    return dispatch(resetExecutionBriefStatus(form.id));
  }

  try {
    dispatch({ type: SET_EXECUTION_BRIEF_LOADING, payload: LOADING_STATES.loading });

    const { data } = await backend.patch(`/execution-briefs/${form.id}/`, params);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({ type: SUCCESS_UPDATE_EXECUTION_PAGE, payload: { ...data } });

    if (form.status === "readyForApproval" && data.status === "draft") {
      try {
        await backend.put(`/execution-briefs/${form.id}/start-review/`);
        const status: ApprovalStatus = "in_progress";
        dispatch(setExecutionBriefStatus(status));
        dispatch(successMessage("Successfully started review process."));
      } catch (error) {
        handleError(error, dispatch, FAILURE_UPDATE_EXECUTION_PAGE);
        dispatch(errorMessage(createError(error).errorMessage));
      }
    } else {
      dispatch(successMessage("Successfully saved multi channel brief."));
    }
  } catch (error) {
    handleError(error, dispatch, FAILURE_UPDATE_EXECUTION_PAGE);
    dispatch(errorMessage(createError(error).errorMessage));
  }
};

export const setExecutionBriefStatus = (status: ApprovalStatus) => ({
  type: SET_EXECUTION_BRIEF_STATUS,
  payload: {
    status,
  },
});

export const setExecutionBriefParentStatus = (parentStatus: ApprovalStatus) => ({
  type: SET_EXECUTION_BRIEF_PARENT_STATUS,
  payload: {
    parentStatus,
  },
});
